import { computed, type ComputedRef } from 'vue';
import { useGetPage } from './useGetPage';
import { useGetPageBlock } from './useGetPageBlock';
import { useGetPageSection } from './useGetPageSection';
import { findFieldByKey, findFieldInBlock, findFieldInDocument } from '../utils/document.utils';
import { getFieldValue } from '../utils/field.accessors';
import { composableTracker } from '../utils/composableTracker';

export interface GetPageFieldParams {
  registryId: string;
  fieldKey: string;
  sectionKey?: string;
  blockKey?: string;
}

/**
 * Get a field value from a page document.
 * Navigation priority: block+section → block → section → page
 * @param params - Object containing registryId, fieldKey, and optional sectionKey/blockKey
 * @returns ComputedRef of field value, or null if page/block/section not loaded or field not found
 */
export const useGetPageField = <T = string>({
  registryId,
  fieldKey,
  sectionKey,
  blockKey,
}: GetPageFieldParams): ComputedRef<T | null> => {
  const trackKey = `${registryId}/${blockKey ?? ''}/${sectionKey ?? ''}/${fieldKey}`;
  composableTracker.track('useGetPageField', 'call', trackKey);

  if (blockKey && sectionKey) {
    const section = useGetPageSection({ registryId, sectionKey, blockKey });
    return computed(() => {
      composableTracker.track('useGetPageField', 'computedEvaluated', trackKey);
      if (!section.value) return null;
      const field = findFieldByKey(section.value, fieldKey);
      return getFieldValue<T>(field);
    });
  }

  if (blockKey) {
    const block = useGetPageBlock({ registryId, blockKey });
    return computed(() => {
      composableTracker.track('useGetPageField', 'computedEvaluated', trackKey);
      if (!block.value) return null;
      const field = findFieldInBlock(block.value, fieldKey);
      return getFieldValue<T>(field);
    });
  }

  if (sectionKey) {
    const section = useGetPageSection({ registryId, sectionKey });
    return computed(() => {
      composableTracker.track('useGetPageField', 'computedEvaluated', trackKey);
      if (!section.value) return null;
      const field = findFieldByKey(section.value, fieldKey);
      return getFieldValue<T>(field);
    });
  }

  const page = useGetPage({ registryId });
  return computed(() => {
    composableTracker.track('useGetPageField', 'computedEvaluated', trackKey);
    if (!page.value) return null;
    const field = findFieldInDocument(page.value, fieldKey);
    return getFieldValue<T>(field);
  });
};
